import type { DataSourceId } from '../../contexts/DataSourceContext';
import type { WelcomeFirstSyncState } from './first-sync';

export type WelcomeMissionReadinessKind =
  | 'ready'
  | 'building'
  | 'repairable_source_blocker'
  | 'terminal_blocker'
  | 'unavailable'
  | 'user_skipped_sources';

export interface WelcomeMissionReadiness {
  kind: WelcomeMissionReadinessKind;
  reasonCode: string;
  nextRoute?: string;
  grantEligible: false;
}

export function evaluateWelcomeMissionReadiness(input: {
  sourceId?: DataSourceId | null;
  firstSyncState?: WelcomeFirstSyncState | null;
  firstSyncReason?: string | null;
  visibleMissionCount?: number;
  missionsGenerated?: boolean;
  userSkippedSources?: boolean;
}): WelcomeMissionReadiness {
  if (input.userSkippedSources) {
    return {
      kind: 'user_skipped_sources',
      reasonCode: 'user-skipped-sources',
      nextRoute: '/connections?welcome=user_skipped_sources',
      grantEligible: false,
    };
  }

  if ((input.visibleMissionCount ?? 0) > 0 || input.missionsGenerated) {
    return {
      kind: 'ready',
      reasonCode: 'missions-ready',
      nextRoute: '/',
      grantEligible: false,
    };
  }

  if (!input.firstSyncState) {
    return {
      kind: 'unavailable',
      reasonCode: 'mission-readiness-unavailable',
      grantEligible: false,
    };
  }

  if (
    input.firstSyncState === 'sync_success'
    || input.firstSyncState === 'zero_item_success'
    || input.firstSyncState === 'building_missions'
  ) {
    return {
      kind: 'building',
      reasonCode: input.firstSyncState === 'building_missions'
        ? 'source-write-observed-building-missions'
        : 'source-sync-complete-building-missions',
      nextRoute: '/?welcome=building_missions',
      grantEligible: false,
    };
  }

  if (input.firstSyncState === 'source_not_connected' || input.firstSyncState === 'token_validation_failed') {
    const route = input.sourceId
      ? `/connections/${input.sourceId}?welcome=repair_source&reason=${input.firstSyncState}`
      : `/connections?welcome=repair_source&reason=${input.firstSyncState}`;
    return {
      kind: 'repairable_source_blocker',
      reasonCode: input.firstSyncReason ?? input.firstSyncState,
      nextRoute: route,
      grantEligible: false,
    };
  }

  return {
    kind: 'terminal_blocker',
    reasonCode: input.firstSyncReason ?? input.firstSyncState,
    grantEligible: false,
  };
}
